import {FeatCategory} from '../../../../generated/types';
import {ABILITIES, SKILLS} from './abilities';
import type {Catalog, ItemData} from './catalog';
import type {Character} from './character';
import type {ChoiceInstance} from './feature-tree';
import {abilityLabel, damageTypeLabel, skillLabel, weaponMasteryLabel} from './labels';
import type {ChooseGroup, ChooseType, LanguageKind} from './types';

export type ChoiceOption = {
	key: string;
	label: string;
};

const DEFAULT_LANGUAGE_KINDS: LanguageKind[] = ['standard'];

const FEAT_CATEGORY_BY_LETTER: Record<string, FeatCategory> = {
	O: FeatCategory.Origin,
	G: FeatCategory.General,
	FS: FeatCategory.FightingStyle,
	EB: FeatCategory.EpicBones,
	PP: FeatCategory.PlanarPact,
};

const SPELL_SCHOOL_BY_LETTER: Record<string, string> = {
	A: 'Abjuration',
	C: 'Conjuration',
	D: 'Divination',
	E: 'Enchantment',
	V: 'Evocation',
	I: 'Illusion',
	N: 'Necromancy',
	T: 'Transmutation',
};

const WEAPON_CATEGORY_BY_LETTER: Record<string, string> = {
	S: 'Simple',
	M: 'Martial',
};

const EPIC_BOON_LEVEL = 19;
const REFERENCE = /^\{([^{}]+)\}$/;

const isInstrument = (item: ItemData) => item.type === 'Tool' && item.subTypes.includes('Instrument');
const isArtisanTool = (item: ItemData) => item.type === 'ArtisansTools';
const isGamingSet = (item: ItemData) => item.type === 'GamingSet';
const isAnyTool = (item: ItemData) => isArtisanTool(item) || isGamingSet(item) || item.type === 'Tool';
const isMasteryWeapon = (item: ItemData) => item.type === 'Weapon' && item.weaponMastery.length > 0;

export function optionLabel(group: ChooseGroup, key: string, catalog: Catalog): string {
	const ownLabel = group.options?.find((option) => option.key === key)?.label;
	if (ownLabel) return ownLabel;
	return labelByType(group.type, key, catalog);
}

function labelByType(type: ChooseType, key: string, catalog: Catalog): string {
	switch (type) {
		case 'ability':
			return abilityLabel(key);
		case 'skill':
		case 'expertise':
			return skillLabel(key);
		case 'feat':
			return catalog.getFeat(key)?.title || key;
		case 'feature':
			return catalog.getFeature(key)?.title || key;
		case 'spell':
			return catalog.getSpell(key)?.title || key;
		case 'subClass':
			return catalog.subClassTitle(key) || key;
		case 'language':
			return catalog.getLanguage(key)?.title || key;
		case 'tool':
		case 'instrument':
		case 'gamingSet':
			return catalog.getItem(key)?.title || key;
		case 'proficiency':
			return SKILLS.includes(key) ? skillLabel(key) : catalog.getItem(key)?.title || key;
		case 'weapon':
			return weaponLabel(key, catalog);
		default:
			return catalog.getClass(key)?.title || catalog.subClassTitle(key) || damageTypeLabel(key);
	}
}

function weaponLabel(weaponId: string, catalog: Catalog): string {
	const weapon = catalog.getItem(weaponId);
	if (!weapon) return weaponId;
	const masteries = weapon.weaponMastery.map(weaponMasteryLabel).join(', ');
	return masteries ? `${weapon.title} (${masteries})` : weapon.title;
}

export function choiceOptions(choice: ChoiceInstance, catalog: Catalog, character: Character): ChoiceOption[] {
	const keys = choice.group.options ? expandReferences(choice, character) : catalogKeys(choice, catalog, character);
	const available = keys.filter(
		(key) => choice.selected.includes(key) || !isTakenElsewhere(key, choice, catalog, character),
	);
	return [...new Set(available)].map((key) => ({key, label: optionLabel(choice.group, key, catalog)}));
}

function expandReferences(choice: ChoiceInstance, character: Character): string[] {
	const scopes = [choice.featureKey, choice.owner.key];
	return (choice.group.options || []).flatMap((option) => {
		const reference = option.key.match(REFERENCE)?.[1];
		return reference ? character.choiceValues(reference, scopes) : [option.key];
	});
}

function catalogKeys(choice: ChoiceInstance, catalog: Catalog, character: Character): string[] {
	const {group} = choice;
	const {filters} = group;

	switch (group.type) {
		case 'ability':
			return ABILITIES;
		case 'skill':
		case 'expertise':
			return SKILLS;
		case 'language': {
			const kinds = filters.language || DEFAULT_LANGUAGE_KINDS;
			return catalog.languages
				.filter((language) => kinds.some((kind) => language[kind]))
				.map((language) => language.id);
		}
		case 'tool':
			return catalog.items
				.filter((item) =>
					filters.artisan
						? isArtisanTool(item)
						: isArtisanTool(item) || isGamingSet(item) || (item.type === 'Tool' && !isInstrument(item)),
				)
				.map((item) => item.id);
		case 'proficiency':
			return [...SKILLS, ...catalog.items.filter(isAnyTool).map((item) => item.id)];
		case 'instrument':
			return catalog.items.filter(isInstrument).map((item) => item.id);
		case 'gamingSet':
			return catalog.items.filter(isGamingSet).map((item) => item.id);
		case 'weapon': {
			const categories = (filters.weaponCategory || []).map(
				(letter) => WEAPON_CATEGORY_BY_LETTER[letter] || letter,
			);
			return catalog.items
				.filter(isMasteryWeapon)
				.filter((item) => !categories.length || categories.includes(item.weaponCategory || ''))
				.map((item) => item.id);
		}
		case 'spell':
			return spellKeys(choice, catalog, character);
		case 'feat': {
			const categories = (filters.featCategory || []).map((letter) => FEAT_CATEGORY_BY_LETTER[letter] || letter);
			return catalog.feats
				.filter((feat) => !categories.length || categories.includes(feat.category))
				.filter((feat) => feat.category !== FeatCategory.EpicBones || character.level >= EPIC_BOON_LEVEL)
				.map((feat) => feat.id);
		}
		case 'feature': {
			const types = [filters.featureType || []].flat();
			return catalog.features
				.filter((feature) => types.includes(feature.type || ''))
				.map((feature) => feature.id);
		}
		case 'subClass': {
			const classData = choice.owner.classId ? catalog.getClass(choice.owner.classId) : undefined;
			return (classData?.subClasses || []).map((subClass) => subClass.id);
		}
		default:
			return [];
	}
}

function spellKeys(choice: ChoiceInstance, catalog: Catalog, character: Character): string[] {
	const {filters} = choice.group;
	const scopes = [choice.featureKey, choice.owner.key];
	const spellLists = (filters.class || []).flatMap((classId) => {
		const reference = classId.match(REFERENCE)?.[1];
		return reference ? character.choiceValues(reference, scopes) : [classId];
	});
	const schools = [filters.school || []].flat().map((letter) => SPELL_SCHOOL_BY_LETTER[letter] || letter);
	const instance = character.getFeatureInstance(choice.featureKey);
	const spellcasting = instance?.feature.props?.spellcasting;
	const maxLevel = spellcasting && choice.owner.classId ? character.maxSpellLevel(choice.owner.classId) : Infinity;
	const allowsCantrips = !spellcasting || !!filters.spellLevel?.includes(0);
	const isWithinReach = (spellLevel: number) => (spellLevel === 0 ? allowsCantrips : spellLevel <= maxLevel);

	if (spellcasting?.mode === 'spellbook' && choice.group.params.prepared) {
		const bookSpells = character.choices
			.filter((other) => other.owner.key === choice.owner.key)
			.filter((other) => other.group.type === 'spell' && !other.group.params.prepared)
			.flatMap((other) => other.selected)
			.concat(choice.owner.classId ? character.spellbookAdditions(choice.owner.classId) : []);
		return [...new Set(bookSpells)].filter((spellId) => {
			const spellLevel = catalog.getSpell(spellId)?.level ?? 0;
			return spellLevel > 0 && spellLevel <= maxLevel;
		});
	}

	return catalog.spells
		.filter((spell) => isWithinReach(spell.level))
		.filter((spell) => !filters.spellLevel?.length || filters.spellLevel.includes(spell.level))
		.filter((spell) => !schools.length || schools.includes(spell.school || ''))
		.filter((spell) => !spellLists.length || spell.classIds.some((classId) => spellLists.includes(classId)))
		.map((spell) => spell.id);
}

/**
 * Повторяемая черта исчерпана, если в её уникальном выборе всё уже взято прежними взятиями:
 * «Посвящённый в магию» с тремя списками заклинаний не взять в четвёртый раз.
 */
function isFeatExhausted(featId: string, catalog: Catalog, otherChoices: ChoiceInstance[]): boolean {
	return catalog
		.featFeatures(featId)
		.flatMap((feature) => feature.choose)
		.filter((featGroup) => featGroup.params.unique && featGroup.options?.length)
		.some((featGroup) => {
			const taken = new Set(
				otherChoices.filter((other) => other.group.key === featGroup.key).flatMap((other) => other.selected),
			);
			return featGroup.options!.every((option) => taken.has(option.key));
		});
}

function isTakenElsewhere(key: string, choice: ChoiceInstance, catalog: Catalog, character: Character): boolean {
	const {group} = choice;
	const otherChoices = character.choices.filter((other) => other.key !== choice.key);
	if (group.params.unique) {
		return otherChoices.some((other) => other.group.key === group.key && other.selected.includes(key));
	}
	if (group.type === 'proficiency') {
		return SKILLS.includes(key)
			? character.skillProficiency(key) !== 'none'
			: [...character.toolProficiencies, ...character.instrumentProficiencies].includes(key);
	}
	if (group.type === 'expertise') return character.skillProficiency(key) !== 'proficient';
	if (group.type === 'skill') return character.skillProficiency(key) !== 'none';
	if (group.type === 'language') return character.languages.includes(key);
	if (group.type === 'instrument') return character.instrumentProficiencies.includes(key);
	if (group.type === 'tool' || group.type === 'gamingSet') return character.toolProficiencies.includes(key);
	if (group.type === 'weapon') return character.weaponMasteries.includes(key);

	if (group.type === 'ability') {
		return otherChoices.some(
			(other) =>
				other.owner.key === choice.owner.key && other.group.key === group.key && other.selected.includes(key),
		);
	}
	if (group.type === 'feat') {
		const isRepeatable = !!catalog.getFeat(key)?.repeatable;
		const isTaken = otherChoices.some((other) => other.group.type === 'feat' && other.selected.includes(key));
		return isTaken && (!isRepeatable || isFeatExhausted(key, catalog, otherChoices));
	}
	if (group.type === 'spell') {
		const isPreparedChoice = !!group.params.prepared;
		return character.knownSpells.some(
			(spell) => spell.id === key && (spell.alwaysPrepared || spell.prepared === isPreparedChoice),
		);
	}
	if (group.type === 'feature') return character.features.some((instance) => instance.feature.id === key);
	return false;
}
